import { useCallback, useEffect, useState, useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Button, ContentCard, LoadingSkeleton, MenuButton, Row, Table } from "@narmi/design_system";
import {
  useLoadingContext,
  useNotificationContext,
} from "cerulean";
import Account from "byzantine/src/Account";
import MemberFriend from "byzantine/src/MemberFriend";
import type { InstitutionFeatures } from "byzantine/src/types";
import { useUserFeatures } from "../../../components/contexts/UserFeaturesContext";
import DeleteDialog from "./DeleteDialog";
import AddMemberDrawer from "../../transfer/AddMemberDrawer";
import RecipientsGrid from "./RecipientsGrid";
import RecipientsTable from "./RecipientsTable";

import type { RecipientsView } from "./RecipientsTable";

interface ManageFriendPopoverProps {
  openDeleteDialog: () => void;
  view: RecipientsView;
}

const ManageFriendPopover = ({
  openDeleteDialog,
  view,
}: ManageFriendPopoverProps) => {
  const { l10n } = useLocalization();

  return (
    <MenuButton
      triggerIcon={view === "list" ? "more-vertical" : "more-horizontal"}
    >
      <MenuButton.Item
        label={l10n.getString("label-view")}
        startIcon="eye"
        // the member details drawer isn't built yet, so View does nothing for now
        onSelect={() => {}}
      />
      <MenuButton.Item
        label="Delete"
        startIcon="trash-2"
        onSelect={openDeleteDialog}
      />
    </MenuButton>
  );
};

interface MemberFriendCardProps {
  memberFriend: MemberFriend;
  fetchMemberFriends: () => Promise<void>;
  view: RecipientsView;
}

// renders the member as a card, or as a table row in list view
const MemberFriendCard = ({
  memberFriend,
  fetchMemberFriends,
  view,
}: MemberFriendCardProps) => {
  const [isDeleteMemberDialogOpen, setIsDeleteMemberDialogOpen] =
    useState(false);
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { l10n } = useLocalization();

  const deleteMember = async () => {
    setIsDeleteMemberDialogOpen(false);
    setIsLoading(true);
    try {
      await memberFriend.delete();
      setIsLoading(false);
      sendNotification({
        type: "success",
        text: l10n.getString("member-deleted"),
      });
      await fetchMemberFriends();
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("member-not-deleted"),
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      {view === "list" ? (
        <Table.Row>
          <Table.Cell>
            <div className="fontColor--heading">{memberFriend.name}</div>
          </Table.Cell>
          <Table.Cell>{memberFriend.displayMaskedAccountNumber()}</Table.Cell>
          <Table.Cell>
            <Row justifyContent="center">
              <ManageFriendPopover
                openDeleteDialog={() => setIsDeleteMemberDialogOpen(true)}
                view={view}
              />
            </Row>
          </Table.Cell>
        </Table.Row>
      ) : (
        <ContentCard kind="elevated">
          <Row>
            <Row.Item>
              <div className="fontColor--heading fontWeight--semibold">
                {memberFriend.name}
              </div>
            </Row.Item>
            <Row.Item shrink>
              <div style={{ marginTop: "-4px" }}>
                <ManageFriendPopover
                  openDeleteDialog={() => setIsDeleteMemberDialogOpen(true)}
                  view={view}
                />
              </div>
            </Row.Item>
          </Row>
          <div className="margin--top--xxs">
            {memberFriend.displayMaskedAccountNumber()}
          </div>
        </ContentCard>
      )}
      <DeleteDialog
        recipientName={memberFriend.name}
        recipientType="member"
        onDelete={deleteMember}
        isOpen={isDeleteMemberDialogOpen}
        closeDialog={() => setIsDeleteMemberDialogOpen(false)}
      />
    </>
  );
};

const MemberFriendSection = ({
  accounts,
  view = "grid",
  isCollapsible = false,
}: {
  accounts: Account[];
  view?: RecipientsView;
  isCollapsible?: boolean;
}) => {
  const [memberFriends, setMemberFriends] = useState<MemberFriend[]>([]);
  const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  const { l10n } = useLocalization();
  const features = useUserFeatures() as InstitutionFeatures;

  const { sendNotification } = useNotificationContext();
  const fetchMemberFriends = async () => {
    setIsFetchingMembers(true);
    try {
      setMemberFriends((await MemberFriend.getMemberFriends()) || []);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("member-fetch-fail"),
      });
    } finally {
      setIsFetchingMembers(false);
    }
  };

  const showAddMemberDialog = useCallback(() => {
    setIsAddMemberDialogOpen(true);
  }, []);

  const hideAddMemberDialog = useCallback(() => {
    setIsAddMemberDialogOpen(false);
  }, []);

  const onAddMember = useCallback(
    (id?: string, error?: string) => {
      if (!error) {
        sendNotification({
          type: "success",
          text: l10n.getString("member-added"),
        });
        fetchMemberFriends();
      } else {
        sendNotification({
          type: "negative",
          text: error,
        });
      }
    },
    [l10n, fetchMemberFriends, sendNotification]
  );

  useEffect(() => {
    fetchMemberFriends();
  }, []);

  const validTransferAccounts = useMemo(
    () => accounts.filter((a) => a.isInternal() && a.isValidM2mSource()),
    [accounts]
  );

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row>
          <Row.Item>
            <div className="fontSize--l fontColor--heading fontWeight--bold">
              {l10n.getString("members-label")}
            </div>
          </Row.Item>
          {!!features.olb_allow_add_member && (
            <Row.Item shrink>
              <Button
                kind="plain"
                size="m"
                startIcon="plus"
                onClick={showAddMemberDialog}
                label={l10n.getString("recipients-add-member-button")}
              />
            </Row.Item>
          )}
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={isFetchingMembers}>
        {view === "list" && memberFriends?.length ? (
          <RecipientsTable
            items={memberFriends}
            columns={[
              {
                label: l10n.getString("label-name"),
                getSortValue: (member) => member.name,
              },
              { label: l10n.getString("label-account") },
            ]}
            renderRow={(member) => (
              <MemberFriendCard
                key={member.id}
                memberFriend={member}
                fetchMemberFriends={fetchMemberFriends}
                view={view}
              />
            )}
          />
        ) : (
          <RecipientsGrid
            items={memberFriends}
            renderItem={(member) => (
              <MemberFriendCard
                key={member.id}
                memberFriend={member}
                fetchMemberFriends={fetchMemberFriends}
                view={view}
              />
            )}
            emptyMessage={l10n.getString("member-empty")}
            isCollapsible={isCollapsible}
          />
        )}
      </LoadingSkeleton>
      <AddMemberDrawer
        accounts={validTransferAccounts}
        newMemberAdded={onAddMember}
        open={isAddMemberDialogOpen}
        handleClose={hideAddMemberDialog}
      />
    </>
  );
};

export default MemberFriendSection;
